import {useAppSelector, useAppDispatch} from "@/store/hooks"
import {
    updateMemberRoleSaga,
    updateMemberStatusSaga,
    deleteInvitationSaga
} from "@/store/workspace/workspaceSlice"
import type {WorkspaceMember} from "@/contexts/WorkspaceContext"
import FormRow from "@/components/ui/Form/components/FormRow/FormRow"
import FormColumn from "@/components/ui/Form/components/FormColumn/FormColumn"
import Avatar from "@/components/ui/Avatar/Avatar"
import DropdownWrapper from "@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper"
import Dropdown from "@/components/ui/Dropdown/Dropdown"
import DropdownItem from "@/components/ui/Dropdown/components/DropdownItem/DropdownItem"
import DropdownDivider from "@/components/ui/Dropdown/components/DropdownDivider/DropdownDivider"
import {TextMidd, TextNormBold} from "@/components/ui/Text"
import ButtonRound from "@/components/ui/ButtonRound/ButtonRound"
import css from "./MemberRow.module.css"

type MemberRole = 'owner' | 'member' | 'guest'

const ROLE_LABELS: Record<MemberRole, string> = {
    owner: 'Owner',
    member: 'Member',
    guest: 'Guest'
}

interface MemberRowProps {
    member: WorkspaceMember
    openRoleDropdownId: string | null
    isCurrentUserOwner: boolean
    onOpenRoleDropdown: (id: string | null) => void
}

const MemberRow = ({member, openRoleDropdownId, isCurrentUserOwner, onOpenRoleDropdown}: MemberRowProps) => {
    const dispatch = useAppDispatch()
    const currentUserId = useAppSelector(state => state.auth?.id)

    const isCurrentUser = member.userId === currentUserId
    const canManage = isCurrentUserOwner && !isCurrentUser

    const handleRoleChange = (newRole: MemberRole) => {
        dispatch(updateMemberRoleSaga({memberId: member.id, role: newRole}))
        onOpenRoleDropdown(null)
    }

    const handleSuspend = () => {
        dispatch(updateMemberStatusSaga({memberId: member.id, status: 'suspended'}))
        onOpenRoleDropdown(null)
    }

    const handleActivate = () => {
        dispatch(updateMemberStatusSaga({memberId: member.id, status: 'active'}))
        onOpenRoleDropdown(null)
    }

    const handleDeleteInvitation = () => {
        dispatch(deleteInvitationSaga(member.id))
        onOpenRoleDropdown(null)
    }

    const buttonLabel = member.status === 'pending' ? 'Pending'
        : member.status === 'suspended' ? 'Suspended'
        : ROLE_LABELS[member.role]

    return (
        <FormRow align={'space-between'} gap={10}>
            <FormRow width={40}>
                <Avatar
                    size={'medium2'}
                    title={member.fullName || member.username}
                    color={member.color}
                    avatar={member.avatarImage}
                />
            </FormRow>
            <FormColumn flex={1} gap={0}>
                <TextNormBold>{member.fullName || member.username}</TextNormBold>
                <TextMidd className={css.member_description}>{member.username} • {member.email}</TextMidd>
            </FormColumn>
            <FormRow>
                <DropdownWrapper>
                    <ButtonRound
                        onClick={() => canManage && onOpenRoleDropdown(
                            openRoleDropdownId === member.id ? null : member.id
                        )}
                        disabled={!canManage}
                    >
                        {buttonLabel}
                    </ButtonRound>
                    <Dropdown
                        isOpen={openRoleDropdownId === member.id}
                        onClose={() => onOpenRoleDropdown(null)}
                    >
                        {member.status === 'pending' ? (
                            <DropdownItem title="Cancel invitation" onClick={handleDeleteInvitation}/>
                        ) : (
                            <>
                                {member.status === 'active' ? (
                                    <>
                                        <DropdownItem title="Owner" onClick={() => handleRoleChange('owner')}/>
                                        <DropdownItem title="Member" onClick={() => handleRoleChange('member')}/>
                                        <DropdownDivider/>
                                        <DropdownItem title="Suspend" onClick={handleSuspend}/>
                                    </>
                                ) : (
                                    <DropdownItem title="Activate" onClick={handleActivate}/>
                                )}
                            </>
                        )}
                    </Dropdown>
                </DropdownWrapper>
            </FormRow>
        </FormRow>
    )
}

export default MemberRow
